import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Send, Sparkles, ShieldCheck, Clock } from 'lucide-react';
import SEO from '../components/SEO';

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Mandatory honeypot field
        })
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }

      const result = await response.json();
      if (result.status === 'ok') {
        navigate('/tak-for-din-henvendelse');
      } else {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Kontakt | Autentisk thai mad i Skibby | Bestil take-away eller en foodtruck"
        description="Du kan i nedenstående formular sende os en direkte besked. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar idag."
      />

      {/* Header Banner */}
      <section className="relative pt-56 pb-16 bg-brand-dark text-white text-left">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/shutterstock-1407453566.jpg"
            alt="Thai Food Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-dark via-brand-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Kontakt
          </h1>
          <p className="text-white/90 font-display text-lg sm:text-xl mt-2 font-semibold">
            Vi glæder os til at høre fra dig
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50 text-brand-dark">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {/* Left Column: Contact Details */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark">
                  Thai Hot Chili
                </h2>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Du kan i nedenstående formular sende os en direkte besked. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar idag.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="flex flex-col divide-y divide-gray-200">
                <div className="py-5 first:pt-0 last:pb-0 flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-black shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm text-brand-dark">Adresse</h3>
                    <p className="text-sm text-gray-600 mt-1">Bymidten 11, 4050 Skibby</p>
                  </div>
                </div>

                <div className="py-5 first:pt-0 last:pb-0 flex items-start gap-4">
                  <Clock className="w-5 h-5 text-black shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm text-brand-dark">Vinteråbningstider (Take-away)</h3>
                    <p className="text-sm text-gray-600 mt-1">Mandag - fredag: 16.00 - 20.00</p>
                  </div>
                </div>

                <div className="py-5 first:pt-0 last:pb-0 flex items-start gap-4">
                  <Phone className="w-5 h-5 text-black shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm text-brand-dark">Telefon (Take-away)</h3>
                    <a href="tel:+4529832959" className="text-base font-bold text-brand-primary hover:underline block mt-1">
                      29 83 29 59
                    </a>
                  </div>
                </div>

                <div className="py-5 first:pt-0 last:pb-0 flex items-start gap-4">
                  <Phone className="w-5 h-5 text-black shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm text-brand-dark">Telefon (Foodtruck & Booking)</h3>
                    <a href="tel:+4552901306" className="text-base font-bold text-brand-primary hover:underline block mt-1">
                      52 90 13 06
                    </a>
                  </div>
                </div>

                <div className="py-5 first:pt-0 last:pb-0 flex items-start gap-4">
                  <Mail className="w-5 h-5 text-black shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm text-brand-dark">E-mail</h3>
                    <a href="mailto:jes.valente@gmail.com" className="text-sm font-bold text-brand-dark hover:text-brand-primary transition-colors block mt-1 break-all">
                      jes.valente@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 text-xs text-gray-500 border-t border-gray-200 pt-6">
                <p>CVR: 31521483</p>
                <a
                  href="https://www.facebook.com/thaichiliaps/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-brand-primary font-semibold hover:underline"
                >
                  <Facebook className="w-4 h-4" />
                  Følg os på Facebook
                </a>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7 bg-white p-8 sm:p-10 rounded-3xl border border-gray-100 shadow-lg flex flex-col gap-6">
              <h3 className="font-display text-xl font-bold text-brand-dark flex items-center gap-2">
                <Send className="w-5 h-5 text-brand-primary" />
                Send os en besked
              </h3>

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Honeypot field (ABSOLUTELY HIDDEN from humans) */}
                <div className="sr-only absolute" style={{ width: 0, height: 0, overflow: 'hidden' }}>
                  <label htmlFor="website_url">Do not fill this field</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Navn <span className="text-brand-primary">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-transparent border-b border-gray-300 rounded-none px-1 py-2.5 text-sm focus:outline-none focus:border-brand-primary transition-all"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Telefonnummer <span className="text-brand-primary">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-transparent border-b border-gray-300 rounded-none px-1 py-2.5 text-sm focus:outline-none focus:border-brand-primary transition-all"
                      placeholder="Dit telefonnummer"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    E-mail <span className="text-brand-primary">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-transparent border-b border-gray-300 rounded-none px-1 py-2.5 text-sm focus:outline-none focus:border-brand-primary transition-all"
                    placeholder="Din e-mailadresse"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Beskrivelse <span className="text-brand-primary">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="bg-transparent border-b border-gray-300 rounded-none px-1 py-2.5 text-sm focus:outline-none focus:border-brand-primary transition-all resize-none"
                    placeholder="Skriv din besked her, f.eks. detaljer om dit arrangement, dato, antal gæster osv."
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-xl text-xs font-medium">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-brand-primary hover:bg-brand-primary/90 disabled:bg-gray-400 text-white font-semibold py-4 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sender besked...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send besked
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
